import { PayloadAction } from '@reduxjs/toolkit';
import {
  ActivateAlertRuleRequest,
  complianceAPI,
  CreateAlertRuleRequest,
  DeactivateAlertRuleRequest,
  DeleteAlertRuleRequest,
  ListAlertRulesRequest,
  UpdateAlertRuleRequest,
} from '@/api';
import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import { alertRulesActions } from '@/redux/actions';
import { alertRulesMappers } from '@/redux/mappers';

function* listAlertRules(
  action: PayloadAction<ListAlertRulesRequest>,
): SagaIterator {
  try {
    const response = yield call(complianceAPI.listAlertRules, action.payload);
    const data = alertRulesMappers.listAlertRules(response);
    yield put(alertRulesActions.setData(data));
  } catch (error: unknown) {
    console.log(error);
  } finally {
    yield put(alertRulesActions.setLoading(false));
  }
}

function* createAlertRule(
  action: PayloadAction<CreateAlertRuleRequest>,
): SagaIterator {
  try {
    const response = yield call(complianceAPI.createAlertRule, action.payload);
    const data = alertRulesMappers.getAlertRule(response);
    yield put(alertRulesActions.addData(data));
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to create alert rule';
    yield put(alertRulesActions.setError(message));
  } finally {
    yield put(alertRulesActions.setLoading(false));
  }
}

function* deleteAlertRule(
  action: PayloadAction<DeleteAlertRuleRequest>,
): SagaIterator {
  try {
    const response = yield call(complianceAPI.deleteAlertRule, action.payload);

    if (response.result) {
      yield put(alertRulesActions.removeData(action.payload.id));
    }
  } catch (error: unknown) {
    console.log(error);
  } finally {
    yield put(alertRulesActions.setLoading(false));
  }
}

function* updateAlertRule(
  action: PayloadAction<UpdateAlertRuleRequest>,
): SagaIterator {
  try {
    const response = yield call(complianceAPI.updateAlertRule, action.payload);
    const data = alertRulesMappers.getAlertRule(response);
    yield put(alertRulesActions.refreshAlertRule(data));
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to update alert rule';
    yield put(alertRulesActions.setError(message));
  } finally {
    yield put(alertRulesActions.setLoading(false));
  }
}

function* activateAlertRule(
  action: PayloadAction<ActivateAlertRuleRequest>,
): SagaIterator {
  try {
    const response = yield call(
      complianceAPI.activateAlertRule,
      action.payload,
    );
    const data = alertRulesMappers.activateAlertRule(response);
    yield put(alertRulesActions.refreshAlertRule(data));
  } catch (error: unknown) {
    console.log(error);
  } finally {
    yield put(alertRulesActions.setLoading(false));
  }
}

function* deactivateAlertRule(
  action: PayloadAction<DeactivateAlertRuleRequest>,
): SagaIterator {
  try {
    const response = yield call(
      complianceAPI.deactivateAlertRule,
      action.payload,
    );
    const data = alertRulesMappers.deactivateAlertRule(response);
    yield put(alertRulesActions.refreshAlertRule(data));
  } catch (error: unknown) {
    console.log(error);
  } finally {
    yield put(alertRulesActions.setLoading(false));
  }
}

export {
  listAlertRules,
  createAlertRule,
  updateAlertRule,
  activateAlertRule,
  deactivateAlertRule,
  deleteAlertRule,
};
